'use client'

import { useT, useFormat } from '@/lib/i18n'
import { useAnomalySummary } from '../anomalies.hooks'

interface Props {
  excludeEaeu: boolean
  mlLevel: string
  hideEmptyRoute: boolean
  htLevel: string
  aScoreLevel: string
  search: string
  scenarioId: number | 'all'
}

export function AnomalySummaryCards({ excludeEaeu, mlLevel, hideEmptyRoute, htLevel, aScoreLevel, search, scenarioId }: Props) {
  const t = useT()
  const f = useFormat()
  const { data, isLoading } = useAnomalySummary({
    exclude_eaeu: excludeEaeu,
    ml_level: mlLevel,
    hide_empty_route: hideEmptyRoute,
    ht_level: htLevel,
    level: aScoreLevel || undefined,
    search: search || undefined,
    scenario_id: scenarioId !== 'all' ? scenarioId : undefined,
  })

  if (isLoading || !data) {
    return (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="animate-pulse rounded-[6px] border border-[var(--border-default)] bg-[var(--bg-surface)] p-4">
            <div className="h-3 w-16 rounded bg-[var(--border-default)]" />
            <div className="mt-3 h-7 w-12 rounded bg-[var(--border-default)]" />
          </div>
        ))}
      </div>
    )
  }

  const fmt = (v?: number | null) => f.int(v ?? 0)

  // id — стабильный ключ React: label зависит от локали.
  const cards = [
    { id: 'pairTotal', label: t('analytics.summary.pairTotal'), value: fmt(data.pair_total), accent: false },
    { id: 'shipmentTotal', label: t('analytics.summary.shipmentTotal'), value: fmt(data.shipment_total), accent: false },
    { id: 'high', label: t('analytics.summary.high'), value: fmt(data.pair_high), accent: true, color: 'var(--risk-high, #ef4444)' },
    { id: 'medium', label: t('analytics.summary.medium'), value: fmt(data.pair_medium), accent: true, color: 'var(--risk-medium, #f59e0b)' },
    { id: 'model', label: t('analytics.summary.model'), value: 'IF + Z + IQR', accent: false },
  ]

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
      {cards.map(c => (
        <div key={c.id} className="rounded-[6px] border border-[var(--border-default)] bg-[var(--bg-surface)] p-4">
          <p className="text-[10px] font-medium uppercase tracking-[0.08em] text-[var(--text-tertiary)]">
            {c.label}
          </p>
          <p
            className="mt-1 text-[22px] font-bold leading-tight"
            style={c.accent && c.color ? { color: c.color } : undefined}
          >
            {c.value}
          </p>
        </div>
      ))}
    </div>
  )
}
